import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference } from '@/helpers';
import { PageHeader, Spinner } from '@/modules/common';
import { Routes } from '@/router/routes';

import { useOverlayDetails } from '../api';
import { OverlayForm } from '../components';
import { OverlayItem } from '../types';

export const OverlayDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();
  const {
    details,
    uploadUrlData,
    isFetching,
    refetchUploadUrl,
    updateOverlayMutation,
  } = useOverlayDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      ...details,
      updatedAt: format(new Date(details.updatedAt), 'yyyy-MM-dd'),
    };
  }, [details]);

  const updateOverlay = async (data) => {
    if (uploadUrlData?.overlayId) {
      try {
        const body = deepDifference<OverlayItem>(data, defaultValues);
        await updateOverlayMutation.mutateAsync({
          ...body,
          id: params.id,
        });
        toast.success(`Overlay “${data.name}” updated successfully!`);
      } catch (error) {
        console.error(error.message);
      }
    }
  };

  const refetchUploadCredentials = async () => {
    try {
      await refetchUploadUrl();
    } catch (error) {
      toast.error('Uh oh! Something went wrong.');
    }
  };

  if (isFetching) {
    return <Spinner />;
  }

  return (
    <OverlayForm
      editMode
      onSubmit={updateOverlay}
      defaultValues={defaultValues}
      uploadUrlData={uploadUrlData}
      refetchUploadCredentials={refetchUploadCredentials}
    >
      {({ isLoading }) => (
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Overlays}
          actions={[
            <Button
              key="save-overlay"
              type="submit"
              data-testid="save-overlay-button"
              isLoading={
                isFetching || isLoading || updateOverlayMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      )}
    </OverlayForm>
  );
};
